import { Component, Injector, OnInit, ViewEncapsulation } from '@angular/core';
import {
  Observable,
  catchError,
  distinctUntilChanged,
  fromEvent,
  lastValueFrom,
  map,
  switchMap,
  take,
  takeUntil,
  throwError,
} from 'rxjs';
import {
  AuthService,
  LogService,
  PrintService,
  StyleService,
  TemplateService,
} from './services';
import { Router } from '@angular/router';
import { BaseComponent, SnackbarComponent } from './components';
import { ProfileService } from './services/profile.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MainTemplate } from './models';
import { availableCms } from '../constants';
import { DataPointService } from './services/data-point.service';
import { TransactionLogService } from './services/transaction-log.service';
import { InsightsLogsService } from './services/insights-logs.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import customMatIcons from "../assets/images/custom-svg.json";
import { LogSource, LogType } from './models/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent extends BaseComponent implements OnInit {
  public isLoaded: boolean = false;
  public isShowLoader: boolean = false;
  public mainTemplate: MainTemplate = {} as MainTemplate;
  public windowResize$: Observable<Event>;
  public initialMainTemplate: string = '';

  constructor(
    public readonly styleService: StyleService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly templateService: TemplateService,
    private readonly profileService: ProfileService,
    private readonly ins: InsightsLogsService,
    private readonly snackBar: MatSnackBar,
    private readonly logService: LogService,
    private dataPointService: DataPointService,
    private transactionLogService: TransactionLogService,
    private printService: PrintService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    injector: Injector
  ) {
    super(injector);
    this.windowResize$ = fromEvent(window, 'resize');
  }

  private divideWidthAndFontSize(tree: any, divisor: number): void {
    const measureProps = [
      'borderWidth',
      'width',
      'borderRadius',
      'radius',
      'fontSize',
    ];
    for (const key in tree) {
      if (typeof tree[key] === 'object') {
        if (Array.isArray(tree[key])) {
          for (const item of tree[key]) {
            this.divideWidthAndFontSize(item, divisor);
          }
        } else {
          this.divideWidthAndFontSize(tree[key], divisor);
        }
      } else if (measureProps.includes(key)) {
        if (Number(tree[key]) > 0) {
          tree[key] = String(Number(tree[key]) / divisor);
        }
      }
    }
  }

  async ngOnInit(): Promise<void> {
    if (window.location.href.includes('carousel')) {
      this.isLoaded = true;
      return;
    }

    //retry any transaction logs that failed during previous offline/network failure
    this.retryPendingTransactionLogs();
    window.addEventListener('online', () => this.retryPendingTransactionLogs());
    
    customMatIcons.imageNames.forEach((val: string) => {
      this.matIconRegistry.addSvgIcon(
        val,
        this.domSanitizer.bypassSecurityTrustResourceUrl(`../assets/images/svg/${val}.svg`)
      );
    })

    const searchUrl = window.location.search;
    const serachParams = new URLSearchParams(searchUrl);
    let kioskId = serachParams.get('kioskId');
    let deviceHash = serachParams.get('h');
    let patronId = serachParams.get('patronId');
    let deviceId = serachParams.get('kioskDeviceId');
    const _this = this;

    console.log(`Before template setup - Kiosk ID: ${kioskId}; Device ID: ${deviceId}; Hash: ${deviceHash}; Patron ID: ${patronId}`);

    this.windowResize$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        map((event: Event) => event)
      )
      .subscribe((event: Event) => {
        if (!_this.initialMainTemplate) return;

        let template = JSON.parse(_this.initialMainTemplate);
        let appendedCssOvverideStyles = document.getElementById('cssOvveride');
        if (!!appendedCssOvverideStyles) {
          appendedCssOvverideStyles?.remove();
        }
        if (_this.styleService.isQt3Kiosk) {
          _this.divideWidthAndFontSize(template, 2);
          _this.templateService.setTemplateData(template);
          _this.addCssOvverideStyles(template.globalVariables.cssOverride);
          template = null;
        } else {
          _this.templateService.setTemplateData(
            JSON.parse(_this.initialMainTemplate)
          );
          _this.addCssOvverideStyles(template.globalVariables.cssOverride);
        }
      });

    if (!kioskId) {
      kioskId = this.authService.kioskId;
    }
    if (!deviceHash) {
      deviceHash = this.authService.deviceHash;
    }
    if (!patronId) {
      patronId = this.authService.externalId;
    }
    if (!deviceId) {
      deviceId = this.authService.deviceId;
    } else {
      this.authService.setDeviceId(deviceId);
    }

    this.authService.removeExitKioskUrl();
    if (!kioskId || !deviceHash || !patronId) {
      console.log('missed values kioskId || deviceHash || patronId', LogType.ERROR, LogSource.SYSTEM)
      this.router.navigate(['404']);
      this.isLoaded = true;
    } else {
      this.authService.setExternalId(patronId);
      this.authService.setDeviceHash(deviceHash);
      this.authService.setKioskId(kioskId);
      this.ins.logTrace('Start to getting token for touchpoint ' + new Date());
      this.authService
        .getToken(kioskId, deviceHash, patronId, deviceId)
        .pipe(
          switchMap((token) => {
            console.log(
              'Start to getting token for touchpoint ' + new Date()
            );
            console.log("Get token: ", token);
            return this.templateService.getTemplate().pipe(
              catchError((templateError) => {
                this.isLoaded = true;
                console.log(
                  'Getting template is failed: ' + JSON.stringify(templateError)
                );
                this.snackBar.openFromComponent(SnackbarComponent, {
                  panelClass: 'error',
                  duration: 25000,
                  horizontalPosition: 'end',
                  data: {
                    type: 'error',
                    message: 'Getting template is failed. Please try again',
                  },
                });
                this.router.navigate(['404']);
                return throwError(templateError);
              })
            );
          }),
          take(1)
        )
        .subscribe(
          async (template) => {
            if (template.advancedSettings.debugLogEnabled) {
              this.logService.setLogEnabled(true);
            }

            this.logService.setLevels(template.advancedSettings.levels);
            this.logService.setSources(template.advancedSettings.debugSources);

            this.logService.writeLog('Template recieved ' + new Date(), LogType.TRACE, LogSource.SYSTEM);
            this.ins.logTrace('Template recieved ' + new Date());
            this.isShowLoader = true;
            this.mainTemplate = template;
            this.initialMainTemplate = JSON.stringify(template);
            this.authService.setExitKioskUrl(
              template.advancedSettings.exitKioskUrl
            );
            const universalConnectorSettings =
              template.advancedSettings.universalConnector;
            this.profileService.universalConnectorSettings =
              universalConnectorSettings;
            const universalConnectorHost =
              this.getUniversalConectorUrlBasedOnDebug(
                serachParams,
                template.advancedSettings
              );
            this.profileService.ucVersion =
              universalConnectorSettings.ucVersion;
            this.profileService.cmsName = template.advancedSettings.activeCMS;
            this.templateService.setTemplateData(template);
            localStorage.removeItem('isExecuteProcessing');
            localStorage.removeItem('isPrintProcessing');
            await this.processDatapointConfigurations();

            this.logService.writeLog(`After template setup - Kiosk ID: ${kioskId}; Device ID: ${deviceId}; Hash: ${deviceHash}; Patron ID: ${patronId}`, LogType.INFO, LogSource.SYSTEM)
            if (patronId) {
              try {
                await this.profileService.getProfile(
                  universalConnectorHost,
                  patronId
                );
                this.logService.writeLog('UC data recieved ' + new Date());
                this.ins.logTrace('UC data recieved ' + new Date());
              } catch (getProfileError) {
                this.isLoaded = true;
                const errorMessage = `Getting profile failed. Host: ${universalConnectorHost}api/PlayerProfile/; PatronId: ${patronId} Error: ${JSON.stringify(
                  getProfileError
                )}`;
                this.logService.writeLog(errorMessage);

                this.snackBar.openFromComponent(SnackbarComponent, {
                  panelClass: 'error',
                  duration: 25000,
                  horizontalPosition: 'end',
                  data: {
                    type: 'error',
                    message: 'Getting profile is failed. Please try again',
                  },
                });
                this.router.navigate(['404']);
                return throwError(getProfileError);
              }
            } else {
              this.logService.writeLog('Patron id is undefined');
              throw new Error('Patron id is undefined');
            }
            this.isLoaded = true;
            console.log(template.template.additionalInfo);
            if (this.styleService.isQt3Kiosk) {
              this.divideWidthAndFontSize(template, 2);
            }

            console.log(template);
            // this.styleService.setHeaders(template.advancedSettings.localResource);
            this.addCssOvverideStyles(template.globalVariables.cssOverride);
            this.styleService.addTierStyles(template);
            this.styleService.applyModalWindowStyles(
              template.components.modalWindow
            );
            this.styleService.applyDropdownStyles(template.components.dropdown);

            this.styleService.getSnackBarStyles(template.components.snackbars);
            this.templateService.setTemplateData(template);
          },
          (err) => {
            this.logService.writeLog(JSON.stringify(err));
            this.snackBar.openFromComponent(SnackbarComponent, {
              panelClass: 'error',
              duration: 25000,
              horizontalPosition: 'end',
              data: {
                type: 'error',
                message: 'Authentication has failed. Please try again',
              },
            });
            this.router.navigate(['404']);
            this.isLoaded = true;
          }
        );
    }
  }

  //retry pending transaction logs saved during offline/network failure
  private async retryPendingTransactionLogs(): Promise<void> {
    const raw = localStorage.getItem('pendingTransactionLogs');
    if (!raw) return;

    let pendingLogs: any[] = [];
    try {
      pendingLogs = JSON.parse(raw);
    } catch {
      localStorage.removeItem('pendingTransactionLogs');
      return;
    }

    if (!pendingLogs.length) return;

    const failed: any[] = [];

    for (const log of pendingLogs) {
      try {
        await this.transactionLogService.postTransactionLog(log);
      } catch {
        failed.push(log);
      }
    }

    if (failed.length) {
      localStorage.setItem('pendingTransactionLogs', JSON.stringify(failed));
    } else {
      localStorage.removeItem('pendingTransactionLogs');
    }
  }

  private async processDatapointConfigurations() {
    this.dataPointService.dataPointConfigurationId =
      availableCms.find(
        (value) =>
          value.cms === this.profileService.cmsName &&
          value.version === this.profileService.ucVersion
      )?.value ?? '';
    this.dataPointService.dataPointsList = await lastValueFrom(
      this.dataPointService.getDataPointsConfiguration()
    );
    this.dataPointService.allDataPoints = this.dataPointService.dataPointsList;
    this.dataPointService.dataPointsList =
      this.dataPointService.dataPointsList.filter((dataPoint) =>
        dataPoint?.cms?.includes(this.dataPointService.dataPointConfigurationId)
      );
    await this.dataPointService.mapDatapointPaths();
    this.dataPointService.mapOldDatapointsPaths();
    await this.dataPointService.getDateRangeList();
  }

  private getUniversalConectorUrlBasedOnDebug(
    searchParams: URLSearchParams,
    advancedSettings: any
  ): string {
    if (
      !advancedSettings.debugMode ||
      !advancedSettings?.debugMode?.debugModeEnabled
    ) {
      return advancedSettings.universalConnector.host;
    }
    const { paramValue, paramName, hostName } = advancedSettings.debugMode;
    const param = searchParams.get(paramName);
    if (param && param == paramValue && hostName) {
      this.printService.isDebugModeEnabled = true;
      return hostName;
    }
    
    return advancedSettings.universalConnector.host;
  }

  private addCssOvverideStyles(styles: string): void {
    if (!styles?.length) return;

    const body = document.getElementsByTagName('body')[0];
    const style = document.createElement('style');
    style.id = 'cssOvveride';
    style.type = 'text/css';
    style.appendChild(document.createTextNode(styles));
    body.appendChild(style);
  }
}
